import { useContext, useEffect, useReducer, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import BatchStudentRatings from "../components/teacher/BatchStudentRatings.jsx";
import BreadCrumb from "../components/breadCrumb/BreadCrumb.jsx";
import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao.jsx";
import EprSummary from "../components/teacher/EprSummary.jsx";
import GradingProfilesReducer from "../utils/GradingProfileReducer.jsx";
import GradeStatusPanel from "../components/teacher/GradeStatusPanel.jsx";
import GroupShare from "../components/teacher/GroupShare.jsx";
import LoadingSvg from "../components/loadingSvg/LoadingSvg.jsx";
import LocationDao from "../dao/LocationDao.js";
import ManageDifferentiation from "../components/teacher/ManageDifferentiation.jsx";
import ManageRoster from "../components/teacher/ManageRoster.jsx";
import SingleStudentRatings from "../components/teacher/singleStudentPage/SingleStudentRatings.jsx";
import StudentSearch from "./StudentSearch.jsx";
import TeacherSidebarMenu from "../components/sidebar/TeacherSidebarMenu.jsx";

import { GlobalContext } from "../components/contextProvider/ContextProvider.jsx";
import { instructionalAreaToGrade } from "../utils/SchoolAdmin/translations";
import { IMPLEMENTATION_GUIDE_URL, QUICK_START_GUIDE_URL } from "../components/header/ResourcesLinks.jsx";

import "../styles/TeacherHome.scss";

/**
 * TeacherHome component serves as the main dashboard for teachers,
 * providing access to grading profiles, rosters, and summary views.
 *
 * @component
 * @returns {JSX.Element}
 */
const TeacherHome = () => {
    const { studentNumber } = useParams();
    const { pathname } = useLocation();
    const { state: globalState } = useContext(GlobalContext);
    const { userDetails, currentTimePeriodDto, schoolYearDto, token, schoolLevelTemplateDto, districtTemplateDto } =
        globalState || {};

    const locationKey = userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationKey;

    const [applicableGrades, setApplicableGrades] = useState(null);

    const initialState = {
        gradingProfiles: [],
        selectedEpr: null,
        fetchProfileFlag: true,
        loader: true,
        navCollapsed: false,
        selectedLink: "HOME/SUMMARY"
    };
    const [state, dispatch] = useReducer(GradingProfilesReducer, initialState);

    /**
     * Retrieves the list of released grades for the current time period.
     */
    const releasedGrades = useMemo(() => {
        if (!schoolLevelTemplateDto) return [];
        const currentTimePeriod = currentTimePeriodDto?.name === "1st Semester" ? "firstSemester" : "secondSemester";
        return (
            schoolLevelTemplateDto[currentTimePeriod]?.gradeToSubjects.filter((grade) => grade.templatesReleased) || []
        );
    }, [schoolLevelTemplateDto, currentTimePeriodDto]);

    /**
     * Determines the teacher's assigned grade if it has been released.
     */
    const teacherGradeIfReleased = useMemo(() => {
        if (!userDetails) return null;
        const instructionalAreaKey = userDetails?.userAttributeDto?.userAttributeMap?.INSTRUCTIONAL_AREA_KEY;
        const teacherGrade = instructionalAreaToGrade[Number.parseInt(instructionalAreaKey, 10)];
        return releasedGrades.find((grade) => grade.gradeLevel === teacherGrade);
    }, [userDetails, releasedGrades]);

    /**
     * Fetch applicable grades for this teacher's school location.
     * Mirrors the same LocationDao fetch used in SchoolAdmin.
     */
    useEffect(() => {
        if (!locationKey || !token) return;
        let isCurrent = true;
        LocationDao({ action: "locationByKeyRead", key: locationKey, token })
            .then((response) => {
                if (isCurrent && response?.data?.payload) {
                    const gradeTags = response.data.payload.tags.filter((tag) => tag.type === "GRADE");
                    setApplicableGrades(gradeTags);
                }
            })
            .catch(console.error);
        return () => {
            isCurrent = false;
        };
    }, [locationKey, token]);

    /**
     * Updates the selected navigation link based on the current route.
     */
    useEffect(() => {
        if (pathname === "/") {
            dispatch({ type: "SET_SELECTED_LINK", payload: "HOME" });
        } else {
            const pathSegments = pathname
                .split("/")
                .filter((seg) => seg && Number.isNaN(Number(seg))) // remove empty strings and numeric segments
                .map((seg) => seg.toUpperCase());

            const selectedLink = pathSegments.join("/");
            dispatch({ type: "SET_SELECTED_LINK", payload: selectedLink });
        }
    }, [pathname]);

    /**
     * Fetches grading profiles when necessary.
     * schoolLevelTemplateDto is undefined while still loading, null when confirmed absent,
     * or an object when present. We wait for the fetch to complete (not undefined) before
     * proceeding, but allow fetching even when there's no school template yet.
     */
    useEffect(() => {
        if (userDetails && schoolYearDto && schoolLevelTemplateDto !== undefined && state.fetchProfileFlag) {
            const options = {
                action: "getGradingProfile",
                guid: userDetails.uid,
                schoolYearKey: schoolYearDto?.key,
                token
            };

            dispatch({ type: "SET_LOADER", payload: true });

            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response) {
                        dispatch({ type: "SET_FETCH_FLAG", payload: false });
                        dispatch({ type: "SET_PROFILES", payload: response.data.payload || [] });
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    dispatch({ type: "SET_LOADER", payload: false });
                });
        }
    }, [schoolYearDto, schoolLevelTemplateDto, state.fetchProfileFlag, token, userDetails]);

    const basePath = "/home";

    return (
        <div className="gutter-95" style={{ padding: "0px 0px" }}>
            <BreadCrumb
                style={{ marginTop: "-10px" }}
                setSelectedLink={(link) => dispatch({ type: "SET_SELECTED_LINK", payload: link })}
            />
            <main className="teacher-home-main">
                <TeacherSidebarMenu dispatch={dispatch} state={state} basePath={basePath} />
                {state.selectedLink === "HOME" && !state.loader && (
                    <div className="teacher-home-wrapper">
                        <div className="home-top-row">
                            <section className="section-container top">
                                <div className="sub-section-title">Welcome to Report Card</div>
                                <div className="sub-section-line" />
                                <div className="updates-container">
                                    <div className="updates-text">
                                        <b>🎉 New Feature:</b> You can now rate differentiated content for multiple
                                        students at once from either Homeroom or Groups — choose{" "}
                                        <b>Differentiated Items</b> as the rating type.
                                    </div>
                                    <div className="updates-text">
                                        Set up student differentiation for multiple students first in{" "}
                                        <b>Manage Differentiation</b>, then return to Homeroom or Groups to bulk rate
                                        differentiated items.
                                    </div>
                                </div>
                            </section>
                            <section className="section-container top home-resources">
                                <div className="sub-section-title">Resources</div>
                                <div className="sub-section-line" />
                                <div className="updates-container">
                                    <div className="updates-text">
                                        Use these guides to get started with Report Card this semester.
                                    </div>
                                    <a
                                        className="home-resource-link"
                                        href={IMPLEMENTATION_GUIDE_URL}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        <i className="bi bi-journal-text" aria-hidden="true" /> Implementation Guide
                                    </a>
                                    <a
                                        className="home-resource-link"
                                        href={QUICK_START_GUIDE_URL}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        <i className="bi bi-lightning-charge" aria-hidden="true" /> Quick Start Guide
                                    </a>
                                </div>
                            </section>
                        </div>
                        <section className="section-container bottom">
                            <div className="sub-section-title">Updates</div>
                            <div className="sub-section-line" />
                            {currentTimePeriodDto?.name === "1st Semester" && !districtTemplateDto && (
                                <div className="updates-container">
                                    <div className="updates-text">
                                        <i
                                            className="bi bi-hourglass-split"
                                            style={{ marginRight: "8px", color: "#C8860A" }}
                                        />{" "}
                                        District template is currently being defined for use by schools. Please check
                                        back later.
                                    </div>
                                </div>
                            )}
                            {currentTimePeriodDto?.name === "1st Semester" && districtTemplateDto && (
                                <GradeStatusPanel
                                    applicableGrades={applicableGrades}
                                    districtTemplateDto={districtTemplateDto}
                                    schoolLevelTemplateDto={schoolLevelTemplateDto}
                                />
                            )}
                        </section>
                    </div>
                )}
                {state.selectedLink.includes("HOME/STUDENT-SEARCH") && !state.loader && <StudentSearch />}
                {state.selectedLink === "HOME/SUMMARY" && !state.loader && (
                    <EprSummary
                        dispatch={dispatch}
                        releasedGrades={releasedGrades}
                        state={state}
                        teacherGradeIfReleased={teacherGradeIfReleased}
                        basePath={basePath}
                    />
                )}
                {state.selectedLink.includes("HOME/SUMMARY/ROSTER") && !state.loader && (
                    <ManageRoster dispatch={dispatch} path={pathname} state={state} basePath={basePath} />
                )}
                {state.selectedLink.includes("HOME/SUMMARY/SHARE") && !state.loader && (
                    <GroupShare dispatch={dispatch} state={state} basePath={basePath} />
                )}
                {state.selectedLink.includes("HOME/SUMMARY/DIFFERENTIATION") && !state.loader && (
                    <ManageDifferentiation dispatch={dispatch} state={state} />
                )}
                {state.selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS") &&
                    !state.loader &&
                    (studentNumber ? (
                        <SingleStudentRatings dispatch={dispatch} state={state} basePath={basePath} />
                    ) : (
                        <BatchStudentRatings dispatch={dispatch} state={state} basePath={basePath} />
                    ))}
            </main>
            {state.loader && <LoadingSvg />}
        </div>
    );
};

export default TeacherHome;
